import React, { useState, useEffect } from "react";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

import * as commonData from "data/common.data";
import { getCurrentLangName } from "helpers/String.helper";

interface KoachSignupStep3Props {
    coachParams: {
        koachTypeCode: string;
        availableLanguageCodes: string[];
    };
    handleInputChange: (section: string, field: string, value: any) => void;
    onValidationChange?: (isValid: boolean) => void;
    termList: any[];
    onChangeCheckbox: (section: string, idx: number, checked: boolean) => void;
    isOnNetworking?: boolean;
}

const KoachSignupStep3: React.FC<KoachSignupStep3Props> = ({
    coachParams,
    handleInputChange,
    onValidationChange,
    termList,
    onChangeCheckbox,
    isOnNetworking = false
}) => {

    const [koachTypeCodeList, setKoachTypeCodeList] = useState<any>([]);

    useEffect(() => {
        setKoachTypeCodeList(commonData.getKoachTypeCodeList());
    }, [])

    // 모든 필수 필드가 입력되었는지 확인
    const isAllRequiredFieldsFilled = () => {
        return (
            coachParams.koachTypeCode.trim() !== "" &&
            coachParams.availableLanguageCodes.length > 0
        );
    };

    // 유효성 검사 상태를 부모 컴포넌트에 전달
    useEffect(() => {
        const isValid = isAllRequiredFieldsFilled();
        onValidationChange?.(isValid);
    }, [coachParams]);

    return (
        <>
            {isOnNetworking && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
                        <svg className="animate-spin h-6 w-6 text-[#CF3688]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                        <span className="text-gray-700">{getLanguageValue(PAGE_TYPE.KOACH, "signupStep3.signupProcessing")}</span>
                    </div>
                </div>
            )}
            
            <section className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4">
                <div className="p-6">
                    <div className="mb-6">
                        <h3 className="text-xl font-semibold text-gray-900">
                            {getLanguageValue(PAGE_TYPE.KOACH, "signupStep3.title")}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {getLanguageValue(PAGE_TYPE.KOACH, "signupStep3.desc")}
                        </p>
                    </div>

                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <span className="text-red-500">*</span>
                                {getLanguageValue(PAGE_TYPE.KOACH, "signupStep3.koachType")}
                            </label>
                            <select
                                value={coachParams.koachTypeCode}
                                onChange={(evt) => handleInputChange("coach", "koachTypeCode", evt.target.value)}
                                disabled={isOnNetworking}
                                className={`block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-base ${isOnNetworking ? 'bg-gray-100 cursor-not-allowed' : ''
                                    }`}
                            >
                                <option value="" disabled selected>
                                    {getLanguageValue(PAGE_TYPE.KOACH, "signupStep3.koachTypePlaceholder")}
                                </option>
                                {commonData.getKoachTypeCodeList().map((item: any, idx: number) => (
                                    <option key={idx} value={item.code}>
                                        {getCurrentLangName(item.names)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <div className="flex items-center">
                                    <span className="text-red-500">*</span>
                                    {getLanguageValue(PAGE_TYPE.KOACH, "signupStep3.koachLanguage")}
                                </div>
                                <em className="text-xs text-gray-500">
                                    {getLanguageValue(PAGE_TYPE.KOACH, "signupStep3.koachLanguageDesc")}
                                </em>
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                                {commonData.getLanguageCodeList().map((language: any) => (
                                    <label key={language.code} className="inline-flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={coachParams.availableLanguageCodes.includes(language.code)}
                                            disabled={isOnNetworking}
                                            onChange={() => {
                                                if (!isOnNetworking) {
                                                    const newLanguages = coachParams.availableLanguageCodes.includes(language.code)
                                                        ? coachParams.availableLanguageCodes.filter(lang => lang !== language.code)
                                                        : [...coachParams.availableLanguageCodes, language.code];
                                                    handleInputChange("coach", "availableLanguageCodes", newLanguages);
                                                }
                                            }}
                                            className={`h-4 w-4 text-[#CF3688] focus:ring-[#CF3688] rounded ${isOnNetworking ? 'opacity-50 cursor-not-allowed' : ''
                                                }`}
                                        />
                                        <span className="ml-2 text-gray-700">{getCurrentLangName(language.names)}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* 약관 목록 */}
            <section className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
                <div className="p-6">
                    <div className="space-y-4">
                        {/* 약관 목록 */}
                        {termList.map((item: any, idx: number) => (
                            <div key={`term-${idx}`} className="flex items-center justify-between">
                                <label className="flex items-center space-x-3">
                                    <input
                                        type="checkbox"
                                        onChange={(evt) => onChangeCheckbox(null, idx, evt.target.checked)}
                                        checked={item.checked}
                                        disabled={isOnNetworking}
                                        className={`h-4 w-4 text-[#CF3688] focus:ring-[#CF3688] rounded ${isOnNetworking ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}
                                    />
                                    <span className="text-sm text-gray-700">
                                        ({item.isRequired
                                            ? getLanguageValue(PAGE_TYPE.COMMON, "studentSignupTerms.txtRequired")
                                            : getLanguageValue(PAGE_TYPE.COMMON, "studentSignupTerms.txtOptional")
                                        }) {item.title}
                                    </span>
                                </label>
                                {item.termsTypeCode !== "termsType:over14" && (
                                    <button
                                        type="button"
                                        disabled={isOnNetworking}
                                        onClick={() => window.open(item.isRequired ? `/term/common/${item.id}` : `/term/etc/${item.id}`)}
                                        className={`text-sm text-[#CF3688] hover:text-[#B52E74] transition-colors ${isOnNetworking ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}
                                    >
                                        ({getLanguageValue(PAGE_TYPE.COMMON, "studentSignupTerms.txtShow")})
                                    </button>
                                )}
                            </div>
                        ))}

                        <div className="pt-4 border-t">
                            <label className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    onChange={(evt) => {
                                        const isChecked = evt.target.checked;
                                        // 모든 약관을 동일한 상태로 설정
                                        termList.forEach((_, idx) => {
                                            onChangeCheckbox(null, idx, isChecked);
                                        });
                                    }}
                                    checked={!termList.find((i: any) => !i.checked)}
                                    disabled={isOnNetworking}
                                    className={`h-4 w-4 text-[#CF3688] focus:ring-[#CF3688] rounded ${isOnNetworking ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                />
                                <span className="text-sm font-medium text-gray-900">
                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupTerms.txtAgreeWithAllTerms")}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default KoachSignupStep3; 